/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Exchange one private asset ID for one short-lived FastAPI-authorized grant.
 * @state-owner TanStack Query memory only
 * @allowed-imports @vispo/api-client
 * @forbidden localStorage; indexedDB; Supabase Storage client; durable signed URLs
 * @public-api private asset presentation
 * @side-effects POST download authorization request
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @tests private-assets.test.ts
 */

import type { VispoApiClient } from '@vispo/api-client';
import { z } from 'zod';

export interface PrivateAssetGrant {
  assetId: string;
  filename: string;
  mimeType: string;
  downloadUrl: string;
  expiresAt: string;
}

const privateAssetGrantSchema = z.object({
  asset_id: z.string().uuid(),
  filename: z.string().min(1),
  mime_type: z.string().min(1),
  download_url: z.string().url(),
  expires_at: z.string().min(1),
});

export async function privateAssetGrant(
  apiClient: VispoApiClient,
  assetId: string,
): Promise<PrivateAssetGrant> {
  const result = privateAssetGrantSchema.parse(
    await apiClient.request<unknown>(`/api/v1/assets/${encodeURIComponent(assetId)}/download-url`, {
      method: 'POST',
    }),
  );
  return {
    assetId: result.asset_id,
    downloadUrl: result.download_url,
    expiresAt: result.expires_at,
    filename: result.filename,
    mimeType: result.mime_type,
  };
}
